import { autoLinkFragment } from "./autoLink";
import { sanitizeToFragment } from "./sanitize";

/**
 * リッチテキスト HTML の加工。
 *
 * 入口は用途ごとに2つ:
 *   renderRichText … DOM へ入れる直前（宿泊客の表示・管理画面の読み込み）
 *   autoLinkHtml   … 保存直前（API へ送る前）
 *
 * サニタイズを renderRichText 側にだけ置いているのは、除去が不可逆なため。
 * 保存経路で削ると、allowlist の漏れで正当な文面を落としたときに元へ戻せない。
 * DOM へ入る手前で必ず通るので、防御としてはこちらで足りる。
 */

// <template> の content は inert な DocumentFragment なので、パースしただけでは
// 画像取得やスクリプト実行が起きない。
function parseInert(html) {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content;
}

function serialize(fragment) {
  const container = document.createElement("div");
  // appendChild でフラグメントの子ノードが container 側へ移動する。
  container.appendChild(fragment);
  return container.innerHTML;
}

/**
 * 保存済みリッチテキスト HTML を、DOM へ入れてよい形に加工する唯一の入口。
 *
 * 適用順は sanitize → autoLink。逆にすると、自動生成した <a> が allowlist で
 * 剥がされる、あるいは除去予定の要素の中身をリンク化してしまう。
 *
 * パースは sanitize が内部で行う1回だけで、その結果の fragment をそのまま
 * autoLink へ渡している。
 *
 * 呼び出し元は以下の2つ。どちらも「保存済みの HTML を DOM へ入れる」経路である:
 *   - checkin/Index.vue … 宿泊客の約款モーダル（v-html）
 *   - terms.vue showText … 管理画面のエディタ読み込み。QuillEditor の value watcher
 *     が root.innerHTML へ生で代入するため、ここを通さないと <img onerror> が
 *     管理者のブラウザで発火する。Quill の正規化は DOM パースの後なので間に合わない。
 *
 * TODO: v-html を使っている他の箇所は本タスクのスコープ外。別途対応が必要:
 *   - MyPage.vue の myPageA / myPageB / myPageC … 入力が b-form-textarea の
 *     プレーンテキストなので、サニタイズではなく {{ }} でのエスケープが正しい対処。
 *   - Step_7.vue の landingText / help.vue の description・answer / ConfirmBox.vue
 *
 * @param {string} html
 * @returns {string} v-html や innerHTML に渡してよい HTML
 */
export function renderRichText(html) {
  if (typeof html !== "string" || html === "") return "";

  const fragment = sanitizeToFragment(html);
  autoLinkFragment(fragment);
  return serialize(fragment);
}

/**
 * 保存する HTML のベタ書き URL をリンクに確定させる。
 *
 * エディタ側（quillAutoLink）のリンク化は入力やフォーカスアウトのタイミングに
 * 依存し、Quill の blur 検出が setTimeout 経由になる経路ではキーボード操作での
 * 保存に間に合わない。保存経路でこれを通すことで、イベント順に関係なく保存内容の
 * リンク化を保証する。
 *
 * 既に <a> になっている箇所は対象外なので、繰り返し適用しても二重にならない。
 * サニタイズは行わない（上記のとおり不可逆なため）。
 *
 * @param {string} html エディタが出力した HTML
 * @returns {string} API へ送る HTML
 */
export function autoLinkHtml(html) {
  if (typeof html !== "string" || html === "") return "";

  const fragment = parseInert(html);
  autoLinkFragment(fragment);
  return serialize(fragment);
}
